import type { BudgetLine } from "@torre/expenses";
import type { ExpectedIncomeMonth } from "../services/expensesService";
import { budgetLinesFor } from "./budget";
import type { TimelineMonth } from "./budgetTimeline";

// ---------------------------------------------------------------------------
// Flujo presupuestado (#1290): el ingreso esperado del CRM (contratos fijos,
// por cobrar y pipeline ponderado) contra el gasto presupuestado, mes a mes.
//
// Ingreso del mes:  pasado   → lo que entró
//                   en curso → lo que ya entró + lo que falta por entrar
//                   futuro   → lo esperado
// Gasto del mes:    pasado   → lo gastado
//                   en curso → el presupuesto (o lo gastado, si ya lo rebasó)
//                   futuro   → el presupuesto
//                   y donde no hay presupuesto, el ritmo de gasto reciente.
// El acumulado arranca en el mes en curso: es lo que falta por pasar.
// ---------------------------------------------------------------------------

/** De dónde sale el gasto del mes. */
export type SpendSource = "actual" | "budget" | "runRate";

export interface ProjectedFlowMonth {
  period: string;
  phase: ExpectedIncomeMonth["phase"];
  /** Ingreso total del mes según su fase. */
  income: number;
  actual: number;
  contracts: number;
  receivable: number;
  pipeline: number;
  /** Cotizaciones enviadas, ponderadas (#1546). Como el pipeline: no es firme. */
  quotes: number;
  spend: number;
  spendSource: SpendSource;
  net: number;
  /** Neto acumulado desde el mes en curso (null en meses pasados). */
  cumulative: number | null;
}

export interface ProjectedFlow {
  months: ProjectedFlowMonth[];
  /** Del mes en curso al final del rango. */
  remaining: {
    months: number;
    income: number;
    /** De ese ingreso, lo que no depende de cerrar negocios: ya entró + contratos + por cobrar. */
    firm: number;
    pipeline: number;
    /** Cotizaciones enviadas ponderadas: dependen de que el cliente acepte. */
    quotes: number;
    spend: number;
    net: number;
    /** Neto si no se cerrara ningún negocio del pipeline ni se aceptara ninguna cotización. */
    netWithoutPipeline: number;
    /** Hay meses cuyo gasto se tomó del ritmo porque no tienen presupuesto. */
    usesRunRate: boolean;
  };
  /** Primer mes (en curso o futuro) cuyo acumulado queda en negativo. */
  firstNegative: string | null;
}

const round = (n: number) => Math.round(n * 100) / 100;

type SpendMonth = Pick<TimelineMonth, "period" | "budget" | "spent" | "expected">;

export function projectedFlow(income: readonly ExpectedIncomeMonth[], spend: readonly SpendMonth[]): ProjectedFlow {
  const spendBy = new Map(spend.map((m) => [m.period, m]));
  let cumulative = 0;
  const months = income.map<ProjectedFlowMonth>((m) => {
    const s = spendBy.get(m.period);
    const spent = s?.spent ?? 0;
    let amount = 0;
    let spendSource: SpendSource = "actual";
    if (m.phase === "past") {
      amount = spent;
    } else if (s && s.budget > 0) {
      amount = m.phase === "current" ? Math.max(s.budget, spent) : s.budget;
      spendSource = "budget";
    } else {
      amount = Math.max(s?.expected ?? 0, m.phase === "current" ? spent : 0);
      spendSource = "runRate";
    }
    const total = m.phase === "past" ? m.actual : m.phase === "current" ? m.actual + m.expected : m.expected;
    const net = round(total - amount);
    if (m.phase !== "past") cumulative += net;
    return {
      period: m.period,
      phase: m.phase,
      income: round(total),
      actual: m.actual,
      contracts: m.contracts,
      receivable: m.receivable,
      pipeline: m.pipeline,
      quotes: m.quotes || 0,
      spend: round(amount),
      spendSource,
      net,
      cumulative: m.phase === "past" ? null : round(cumulative),
    };
  });
  const ahead = months.filter((m) => m.phase !== "past");
  const sum = (pick: (m: ProjectedFlowMonth) => number) => round(ahead.reduce((s, m) => s + pick(m), 0));
  const incomeAhead = sum((m) => m.income);
  const pipeline = sum((m) => m.pipeline);
  const quotes = sum((m) => m.quotes);
  const spendAhead = sum((m) => m.spend);
  return {
    months,
    remaining: {
      months: ahead.length,
      income: incomeAhead,
      firm: round(incomeAhead - pipeline - quotes),
      pipeline,
      quotes,
      spend: spendAhead,
      net: round(incomeAhead - spendAhead),
      netWithoutPipeline: round(incomeAhead - pipeline - quotes - spendAhead),
      usesRunRate: ahead.some((m) => m.spendSource === "runRate" && m.spend > 0),
    },
    firstNegative: ahead.find((m) => (m.cumulative ?? 0) < 0)?.period ?? null,
  };
}

/** Presupuesto de gasto de un mes: sus líneas mensuales y las anuales prorrateadas. */
export function monthBudget(lines: readonly BudgetLine[], period: string): number {
  return round(budgetLinesFor(lines, period).reduce((s, l) => s + l.amount, 0));
}
